import {LTServices} from "../lt-ng/lt-services";
import {L} from "../lt-ng/landcare-toolkit";
import {
    FeatureQueryClickInteraction,
    IMapClickInteractionOptions,
    MapClickInteraction
} from "../lt-ng/map-interactions";
import {PinAndExtras} from "./PinApi";
import {ICoordinateAdjustmentService} from "./coordinate-adjustment";
import {IContextLayerQuery} from "../utility/geo/context-layer-query";
import {getContextQueryForLayer} from "../utility/geo/helpers";
import {IToastService, TOAST_DURATION_LONG} from "./ToastService";
import {ILayerQueryRequest, ILayerQueryService, IRequestAndCancel} from "./layer_query";
import {_F} from "../utility/func";
import {IMapShareData} from "./MapShareAPI";
import {IPortalServices, IRequestError} from "../lt-ng/portal-services";
import {Panes} from "../controllers/panes";
import {ISavedFeatureService} from "./features-api/saved_features";
import {FeatureAndUiState, FeatureKind, UserFeature} from "./features-api/models";
import {FeaturesEvents} from "./events/FeaturesEvents";
import {GeoAppEvents} from "../controllers/events/geoapp_events";
import {SavedFeatureServiceEvents} from "./features-api/events";
import {IUsageLoggingService} from "./IUsageLoggingService";
import {IAppConfig} from "./app-config/app_config";
import {Feature, MapBrowserEvent} from "ol";
import SimpleGeometry from "ol/geom/SimpleGeometry";
import {Coordinate} from "ol/coordinate";
import {GeoJSON, WKT} from "ol/format";
import Point from "ol/geom/Point";
import {Geometry, MultiPolygon, Polygon} from "ol/geom";
import {fromExtent} from "ol/geom/Polygon";
import {Draw, Interaction, Select} from "ol/interaction";
import VectorLayer from "ol/layer/Vector";
import {pointerMove, singleClick} from "ol/events/condition";
import {WmtsGetFeatureInfoInteraction} from "../lt-ng/wmts-getfeatureinfo-interaction";
import {WMTS} from "ol/source";
import {ILayerTreeManipulationService} from "./oe/layer-tree-manipulation";
import {
    AddFeatureMode,
    AreaReport,
    AreaReportFeatureKind,
    AreaReportRequestAndLayers, AreaReportResult,
    CustomLayerAreaReport,
    DrawnAreaReport,
    ExtentAreaReport,
    FeatureReport,
    IPointReport,
    ISharedFeatureControlState,
    MultiplePinReport,
    PinReport,
    PointReportRequestAndLayers,
    PredefinedAreaReport,
    ReportKind
} from "./ISharedFeatureState";
import IMapService = LTServices.IMapService;
import Item = L.tree.Item;
import combineFeatures = _F.combineFeatures;
import getLayerId = L.tree.getLayerId;
import getLayerName = L.tree.getLayerName;
import extractEPSGNum = _F.extractEPSGNum;
import getVisibleScienceLayers = L.tree.getVisibleScienceLayers;
import isQueryAble = L.tree.isQueryAble;
import getLayerById = L.tree.getLayerById;
import arraysContainSameStuff = _F.arraysContainSameStuff;
import arrayHead = _F.arrayHead;
import getFeatureTitleProperty = L.tree.getFeatureTitleProperty;
import clickOutsideOfMapHandler = _F.clickOutsideOfMapHandler;
import pinTitleFromCoordinate = _F.pinTitleFromCoordinate;
import getLayersForAreaQuery = L.tree.getLayersForAreaQuery;
import isCustomLayerQueryable = L.tree.isCustomLayerQueryable;
import filterNodes = L.tree.filterNodes;
import isLocalData = L.tree.isLocalData;
import {mapAreaResponse} from "../utility/mapAreaResponse";

export interface IFeatureState {
    sharedState(): ISharedFeatureControlState;
    toggleMode(mode: AddFeatureMode, layer: Item);
    queryDrawnArea();
    queryPredefinedFeatures();
    queryExtent();
    addPointQuery(coordinate: Coordinate): PinReport;
    getAreaQueryResultsForCurrentScienceLayer() : AreaReportRequestAndLayers;
    getPointQueryResultsForCurrentScienceLayer(): PointReportRequestAndLayers;
    hideReport();
    activeScienceLayersKey(): string;
    layerIsLoading(layer: Item) : boolean;
    getScienceLayersForAreaQuery(): Item[];
    isPickFeatureInteractionActive(): boolean;
    reportingLayers(): Item[];
    setReportingLayers(layers: Item[]);
    toggleSourceLayer(): void;
    showReport(report: FeatureReport);

    createReportFromCustomFeatures(features: Feature<Geometry>[],
                                   customLayerNode: L.tree.Item,
                                   featureAndUiState?: FeatureAndUiState) : CustomLayerAreaReport;

    showReportForFeature(feature: FeatureAndUiState): void;
    saveActiveReportFeatures() : ng.IPromise<void>;
    makePointQueryRequest(rp : PinReport, layers: Item[]) : PointReportRequestAndLayers;

    getActiveReportIfPoint() : PinReport;
    getActiveReportIfMultiplePins(): MultiplePinReport;
    getActiveReportIfArea() : AreaReport;
    getActiveReportIfExtent() : ExtentAreaReport;
    getActiveReportIfDrawn() : DrawnAreaReport;
    getActiveReportIfPredefined() : PredefinedAreaReport;
    reportIfSingle(): PinReport | AreaReport;
    defaultTitleForReport(): string;
    toggleIfLayerIsIncludedInReport(item: Item, toggleOnOrOff: boolean);
    selectPinReport(pinReport: PinReport);
    removeReport(report: FeatureReport);
    dismissReport(report: FeatureReport);
    deselectPins(): void;
}

const ss = (window as any).ss as any;
const ADD_FEATURES = ss && ss.i18n._t('App.ADD_FEATURES', 'Get Reports');
const FEATURE_REPORT = ss && ss.i18n._t('App.FEATURE_REPORT', 'Feature Report');

const QUERIED_REVISION = 'fsQueriedRevision';

export class FeatureState implements IFeatureState {
    protected static ToastId = "FeatureStatue:toast";

    protected activeInteractions: Interaction[] = [];
    protected _reportingLayers: Item[] = [];

    public static $inject = [
            'landcareMapService',
            'layerQueryService',
            'coordinateAdjustmentService',
            '$rootScope',
            'toastService',
            '$http',
            'portalServices',
            '$q',
            'savedFeatureService',
            '$timeout',
            'usageLoggingService',
            'appConfig',
            'layerTreeManipulationService'
    ];
    constructor(protected mapService: IMapService,
                protected layerQueryService : ILayerQueryService,
                protected coordinateAdjustmentService: ICoordinateAdjustmentService,
                protected $rootScope: ng.IRootScopeService,
                protected toastService: IToastService,
                protected $http: ng.IHttpService,
                protected portalServices: IPortalServices,
                protected $q: ng.IQService,
                protected savedFeatureService: ISavedFeatureService,
                protected $timeout: ng.ITimeoutService,
                protected usageLoggingService: IUsageLoggingService,
                protected appConfig: IAppConfig,
                protected layerTreeManipulationService: ILayerTreeManipulationService) {



        this.$rootScope.$on('features:backToStart', () => {
            this.savedFeatureService.updateFeatureVisibility(f => f.userFeature.geometryType == "Point");
        });

        this.$rootScope.$on('selectedPin:change', (event, info: { oldValue : Feature<Point>, newValue : Feature<Point>}) => {
            if (info.newValue) {
                let currentReport = this.getActiveReportIfPoint();
                if (currentReport && currentReport.point == info.newValue) {
                    // If we're already displaying this report, don't do anything
                    // TODO - replace this dirty hack
                    return;
                }

                let savedFeature: FeatureAndUiState = this.savedFeatureService.getUserFeatureFromMapFeature(info.newValue);

                if (savedFeature) {
                    this.showReportForFeature(savedFeature);
                    return;
                }

                this.displayPointQuery(info.newValue);
            }
        });

        this.$rootScope.$on(SavedFeatureServiceEvents.featureDeleted, (ev, feature: FeatureAndUiState) => {
            if (!feature) return;

            let rp = this.sharedState().activeReport;
            if (rp && 'userFeatures' in rp && rp.userFeatures == feature) {
                this.showReport(null);
            }
        });

        this.$rootScope.$on(GeoAppEvents.featureClicked, (ev, feature: Feature<Geometry>) => {
            // Don't do anything, we're in a mode where the user may be clicking on features to edit or remove them
            // This shouldn't be the case though...
            if (this.sharedState().featureMode) return;

            let userFeature : FeatureAndUiState = this.savedFeatureService.getUserFeatureFromMapFeature(feature);
            if (userFeature) this.showReportForFeature(userFeature);
        });

        this.$rootScope.$on(GeoAppEvents.displayShare, (ev, mapShareData: IMapShareData) => {
            if (mapShareData.geometryWKT) {
                let features: Feature<Geometry>[] = [];

                switch (mapShareData.geometryFormat) {
                    case 'GeoJSON':
                        let geojsonFmt = new GeoJSON();
                        try {
                            features = geojsonFmt.readFeatures(mapShareData.geometryWKT)
                        } catch (e) {
                            console.error('FeatureState - failed to parse GeoJSON features from map share');
                        }
                        break;
                    default: // implicit WKT
                        let format = new WKT();
                        try {
                            let geom = format.readGeometry(mapShareData.geometryWKT);
                            if (geom) features = [new Feature(geom)];
                        } catch (e) {
                            console.error('FeatureState - failed to passed WKT features from map share')
                        }
                }

                if (features.length) {
                    features.forEach(f => f.set('selected', true));

                    let firstGeom = features[0].getGeometry();

                    this.savedFeatureService.featuresSourceReady().then((src) => {
                        features.forEach((f, i) => {
                            f.setId(`shared-0:${i}`)
                        });

                        src.addFeatures(features)
                    });

                    if (firstGeom instanceof Point) {
                        this.showReportForFeature({
                            visible: true,
                            deleting: false,
                            userFeature: {
                                id: null,
                                kind: 'custom',
                                title: 'Shared pin',
                                geometryType: 'Point',
                                srid: 2193,
                                featureCollection: JSON.parse(mapShareData.geometryWKT)
                            },
                            mapFeatures: features
                        });

                        return;
                    }

                    let geometry = combineFeatures(features as Feature<Polygon>[]);



                    let sourceLayers = features.map((f) => f.get('sourceLayerId'))
                        .map(id => getLayerById(this.mapService.treeData, id))
                        .map((ls) => ls && ls.length ? ls[0] : null)
                        .filter(n => n)
                        .reduce((layers, layer) => {
                            if (layers.indexOf(layer) == -1) {
                                layers.push(layer);
                            }
                            return layers;
                        }, []);

                    let report: AreaReport;

                    if (sourceLayers.length) {
                        report = <PredefinedAreaReport> {
                            kind: ReportKind.area,
                            featureKind: AreaReportFeatureKind.predefined,
                            revision: 0,
                            geometry,
                            features,
                            queryResults: {},
                            sourceLayer: sourceLayers[0]
                        }
                    } else {
                        report = <DrawnAreaReport> {
                            kind: ReportKind.area,
                            geometry,
                            featureKind: AreaReportFeatureKind.drawn,
                            features,
                            queryResults: {}
                        };
                    }

                    this.showReport(report);

                    this.$timeout(() => {
                        this.mapService.map.getView()
                            .fit(geometry, {
                                size: this.mapService.map.getSize(),
                                padding: this.coordinateAdjustmentService.padding([5,5,5,5])
                            });
                    }, 150);
                }
            }
        });

        let updateAreaReport = (rp: AreaReport, newKind: FeatureKind = null) => {
            // Trigger an update on an area report if the features it contains have changed.

            let fs = rp.features;
            rp.revision = (rp.revision || 0) + 1;
            rp.geometry = fs.length == 1 ? <SimpleGeometry> fs[0].getGeometry() : combineFeatures(fs as Feature<Polygon>[]);
            rp.queryResults = {};

            if (rp.userFeatures) {
                const ufs = rp.userFeatures;
                ufs.mapFeatures = fs;
                // recalculate the GeoJSON attached to the feature
                this.savedFeatureService.updateGeoJSON(ufs, this.mapService.map.getView().getProjection());
                // persist the changes back to the server.
                if (newKind) ufs.userFeature.kind = newKind;
                this.savedFeatureService.saveFeature(ufs, ['kind', 'featureCollection']);
            }
        };

        // Respond to the addition of new features by adding them to the report.
        this.$rootScope.$on(FeaturesEvents.featuresAdded, (event, features: Feature<Geometry>[], doUpdate: boolean) => {
            if (!features || !features.length) return;

            let rp = this.getActiveReportIfArea();
            if (!rp) return;

            let currentFeatureIds = (rp.features || []).map((f) => f.getId());

            // don't want to add features to the report that it already has
            features = features.filter((f) => {
                let fid = f.getId();
                return !fid || currentFeatureIds.indexOf(fid) === -1;
            });

            if (!features.length) {
                // all of the features to add have been filtered out, don't need to trigger any sort of update
                return;
            }

            rp.features.push(...features);
            if (doUpdate) {
                updateAreaReport(rp);
                this.reportChanged();
            }
        });

        this.$rootScope.$on(FeaturesEvents.featuresEdited, () => {
            // if the features in the active report are modified,
            // then we need to update the current area query results
            let rp = this.getActiveReportIfArea();
            if (!rp) return;

            rp.featureKind = AreaReportFeatureKind.drawn;

            if (rp.features.length) {
                updateAreaReport(rp, 'custom');
                this.reportChanged();
            } else {
                this.hideReport();
            }
        });

        this.$rootScope.$on(GeoAppEvents.resetMapAndLayers, () => {
            this.toggleMode(null);
            this._reportingLayers = [];
            this.hideReport();
        })

        this.$rootScope.$on(FeaturesEvents.hideReport, () => this.hideReport());

        this.$rootScope.$on(FeaturesEvents.featureRemoved, (event, feature: Feature<Geometry>, doUpdate: boolean) => {
            let rp = this.sharedState().activeReport;
            if (!rp) return;

            if (rp.kind == ReportKind.area) {
                const fs = rp.features;
                if (fs.indexOf(feature) === -1) {
                    return;
                }

                rp.features = rp.features.filter((f) => f!== feature);
                if (!rp.features.length) {
                    this.hideReport();
                } else if (doUpdate) {
                    updateAreaReport(rp);
                    this.reportChanged();
                }
            } else if (rp.kind == ReportKind.pin && rp.point == feature) {
                this.hideReport();
            } else if (this.getActiveReportIfMultiplePins() && feature.get('pinReport')) {
                this.removeReport(feature.get('pinReport'));
            }
        });

        this.$rootScope.$on(GeoAppEvents.layerToggled, (/*event, data: Item*/) => {
            this.setReportingLayers(getVisibleScienceLayers(this.mapService.treeData));
            if (this.sharedState().activeQueryLayer && !this.sharedState().activeQueryLayer.checked) {
                this.toggleMode(null);
            }
        });
    }

    public reportingLayers(): Item[] {
        return this._reportingLayers;
    };

    protected _sharedState : ISharedFeatureControlState = {
        paneTitle: ADD_FEATURES,
        featureMode: null,
        activeQueryLayer: null,
        activeReport: null,
        featureLoading: false,
        fabMenuOpen: false
    };

    sharedState(): ISharedFeatureControlState {
        return this._sharedState;
    }

    setReportingLayers(layers: L.tree.Item[]) {
        let changed = !arraysContainSameStuff(layers, this._reportingLayers);
        this._reportingLayers = layers;
        if (changed) this.reportChanged();
    }

    protected reportChanged() {
        let rp = this._sharedState.activeReport;
        if (!rp) return;

        if (rp.kind == ReportKind.area) {
            rp.revision = (rp.revision || 0) + 1;
            rp.queryResults = {};
        }

        this.$rootScope.$broadcast(FeaturesEvents.reportChanged, rp);
    }

    activeScienceLayersKey(): string {
        try {
            return FeatureState.layersToKey(this.getScienceLayersForAreaQuery());
        } catch (e) {

        }
        return null;
    }

    getAreaQueryResultsForCurrentScienceLayer(): AreaReportRequestAndLayers {
        let rp = this.sharedState().activeReport;
        if (rp && rp.kind == ReportKind.area) {
            let key = this.activeScienceLayersKey();
            if (!key) return null;

            if (rp.queryResults[key]) return rp.queryResults[key];

            return this.makeAreaQueryRequest(rp, this.getScienceLayersForAreaQuery());
        }

        return undefined;
    }

    toggleIfLayerIsIncludedInReport(item: Item, toggleOnOrOff: boolean) {
        if (toggleOnOrOff) {
            let currentlyActive = this.reportingLayers();
            let toEnable = [item];
            if (!item.xorGroup && !currentlyActive.some((l) => !!l.xorGroup)) {
                toEnable = toEnable.concat(currentlyActive);
            }

            this.setReportingLayers(toEnable);
        } else {
            let othersChecked = this.reportingLayers()
                .filter((i) => i !== item);
            if (othersChecked.length) {
                this.setReportingLayers(othersChecked);
            }
        }
    }

    layerIsLoading(layer: Item) : boolean {
        try {
            return layer == this._sharedState.activeQueryLayer && this.activeInteractions.some(int => int.get('loading'));
        } catch (e) { }

        return false;
    }

    protected displayFeaturesPane() {
        this.$rootScope.$broadcast(Panes.Events.SHOW_PANE, Panes.Ids.FEATURES);
    }

    toggleMode(mode, layer = null) {
        if (this.activeInteractions.length) {
            this.activeInteractions.forEach(ai => {
                this.mapService.map.removeInteraction(ai);
                if (ai instanceof MapClickInteraction) ai.deactivate();
            })
            this.activeInteractions = [];
            this.mapService.setActiveClickInteraction(this.mapService.getDefaultClickInteraction());
        }

        let state = this.sharedState();
        if (mode == state.featureMode && layer == state.activeQueryLayer) {
            mode = null;
            if (layer) this.layerTreeManipulationService.toggleLayer(layer, false, { silent: true });
            layer = null;
        }

        state.featureMode = mode;
        state.activeQueryLayer = mode == 'predefined' || mode == 'customLayer' ? layer : null;

        if (state.featureMode == 'pin') {
            this.queryPoint();
        }

        if (state.featureMode == 'drawn') {
            this.queryDrawnArea();
        }

        if (state.featureMode == 'extent') {
            this.queryExtent();
        }

        if (state.featureMode == 'predefined') {
            this.queryPredefinedFeatures();
        }

        if (state.featureMode == 'customLayer') {
            this.queryCustomLayer();
        }
    };

    pointQueryInteractionOptions : IMapClickInteractionOptions = {
        onClick: (event: MapBrowserEvent<any>) => {
            let existingPoint = false;
            this.$rootScope.$broadcast(Panes.Events.SHOW_PANE, Panes.Ids.FEATURES);

            this.mapService.map.forEachFeatureAtPixel(event.pixel, (feature) => {
                const maybePinReport = feature.get('pinReport') || {};
                if ('kind' in maybePinReport && maybePinReport.kind === ReportKind.pin) {
                    this.selectPinReport(maybePinReport);
                    existingPoint = true;
                    return true;
                }
                return false;
            });

            if (existingPoint) return true;

            let displayPin = () => {
                if (!this.appConfig.multiplePinsInRHP()) {
                    this.mapService.setActiveClickInteraction(this.mapService.getDefaultClickInteraction());
                }

                this.addPointQuery(event.coordinate);

                // implicitly, displayPointQuery is called by the selectedPin:change event handler
                // set up in the constructor
                // this.displayPointQuery(pinData);
            };
            if (!this.$rootScope.$$phase) {
                this.$rootScope.$apply(displayPin)
            } else {
                displayPin();
            }

            return true;
        }
    };

    pointQueryInteraction = new MapClickInteraction(this.pointQueryInteractionOptions);

    queryPoint() {
        this.mapService.setActiveClickInteraction(this.pointQueryInteraction);
    }

    addPointQuery(coord: Coordinate, userFeatures: FeatureAndUiState = undefined, logAsNewPin=true): PinReport {
        if (logAsNewPin) {
            this.usageLoggingService.logWithAnalytics("NEW_PIN", `New Pin: ${coord.join(', ')}`, '');
        }

        const mfs = userFeatures && userFeatures.mapFeatures || [];

        const title = userFeatures && userFeatures.userFeature && userFeatures.userFeature.title || pinTitleFromCoordinate(coord);
        const feature: Feature<Point> = mfs.length ? mfs[0] as Feature<Point> : new Feature(new Point(coord));

        const pinData : PinAndExtras = {
            hidden: false,
            checked: false,
            data: {
                x: coord[0],
                y: coord[1],
                title
            }
        };

        if (!userFeatures) {
            this.mapService.featuresLayer.getSource().addFeature(feature);
            feature.set('pin', pinData);
            feature.set('selected', true);


            userFeatures = {
                visible: true,
                saving: false,
                deleting: false,
                userFeature: null,
                mapFeatures: [feature]
            };

            this.savedFeatureService.createNewFeature(
                title,
                [feature],
                'custom',
                this.mapService.map.getView().getProjection())
                .then((userFeature: UserFeature) => {
                    userFeatures.userFeature = userFeature;
                });
        }

        const result: PinReport = {
            kind: ReportKind.pin,
            point: feature,
            queryResults: {},
            userFeatures
        };


        feature.set('pinReport', result);
        this.selectPinReport(result);

        return result;
    }

    selectPinReport(pinReport: PinReport) {
        if (this.getActiveReportIfArea()) {
            this.hideReport();
        }

        const mapFeatures = pinReport && pinReport.userFeatures && pinReport.userFeatures.mapFeatures || [];

        mapFeatures.forEach(f => f.set('selected', true));
        if (!this.reportingLayers().length) this.useCurrentScienceLayersForReport();
        this.makePointQueryRequest(pinReport, this.getScienceLayersForPointQuery());

        if (this.appConfig.multiplePinsInRHP()) {
            const rp = this.makeActiveReportIfMultiplePin(true);
            rp.reports = [pinReport, ...rp.reports.filter(r => r!==pinReport)];
            const MAX_POINT_REPORTS = 3;
            if (rp.reports.length > MAX_POINT_REPORTS) {
                const toBeRemoved = rp.reports.slice(3);
                rp.reports = rp.reports.slice(0, 3);
                toBeRemoved.forEach((p: PinReport) => {
                    try {
                        (p && p.userFeatures && p.userFeatures.mapFeatures || [])
                            .forEach(f => {
                                f.set('selected', false);
                                f.set('hover', false);
                            });
                    } catch (e) { }
                })
            }
            this.queryPoint();
        } else {
            if (this.getActiveReportIfPoint()) {
                const ap = this.getActiveReportIfPoint();
                ap.userFeatures.mapFeatures.forEach(f => f.set('selected', false));
            }

            this.sharedState().paneTitle = FEATURE_REPORT;
            this.sharedState().activeReport = pinReport;
            this.reportChanged();
        }
    }

    deselectPins() {
        const rp = this.getActiveReportIfMultiplePins() || this.getActiveReportIfPoint();
        if (rp && rp.kind === ReportKind.multiplePin) {
            rp.reports.forEach(r => this.removeReport(r));
        } else if (rp && rp.kind === ReportKind.pin) {
            this.removeReport(rp);
        }
    }

    dismissReport(report: FeatureReport) {
        if (report === this.sharedState().activeReport) {
            this.hideReport();
        } else if (report.kind === ReportKind.pin && this.getActiveReportIfMultiplePins()) {

            report.userFeatures.mapFeatures.forEach(p => p.set('selected', false));

            const mpr = this.getActiveReportIfMultiplePins();
            mpr.reports = mpr.reports.filter(pr => pr !== report);
            if (!mpr.reports.length) {
                this.hideReport();
            }
        }
    }

    removeReport(report: FeatureReport) {
        if (report === this.sharedState().activeReport) {
            this.hideReport();
        } else if (report.kind === ReportKind.pin && this.getActiveReportIfMultiplePins()) {
            const multiplePinReport = this.getActiveReportIfMultiplePins();

            const mapFeatures = report.userFeatures && report.userFeatures.mapFeatures || [];
            if (report && report.userFeatures && report.userFeatures.userFeature && report.userFeatures.userFeature.id) {
                mapFeatures.forEach(f => f.set('selected', false));
            } else {
                mapFeatures.forEach(f => {
                    try {
                        if (this.savedFeatureService.featuresSource.hasFeature(f)) {
                            this.savedFeatureService.featuresSource.removeFeature(f);
                        }
                    } catch (e) {
                        console.warn(e);
                    }
                });
            }

            multiplePinReport.reports = multiplePinReport.reports.filter(r => r!==report);
            if (!multiplePinReport.reports.length) {
                this.hideReport();
            }
        }
    }

    makeActiveReportIfMultiplePin(replaceIfNotMultiPin: boolean): MultiplePinReport|null {
        const rp = this.sharedState().activeReport;
        if (rp && rp.kind === ReportKind.multiplePin) {
            return rp;
        } else if (replaceIfNotMultiPin) {
            const newMultiPinReport: MultiplePinReport = {
                kind: ReportKind.multiplePin,
                reports: []
            };

            this._sharedState.paneTitle = FEATURE_REPORT;
            this._sharedState.activeReport = newMultiPinReport;
            return newMultiPinReport;
        }

        return null;
    }

    saveActiveReportFeatures() : ng.IPromise<void> {
        const report = this.sharedState().activeReport;

        if ('userFeatures' in report && report.userFeatures) {
            return this.savedFeatureService.saveFeature(report.userFeatures);
        }

        let features: Feature<Geometry>[];
        let kind: FeatureKind;
        let sourceLayerId: number = 0;

        if (report.kind == ReportKind.area) {
            features = report.features;
            kind = report.featureKind == AreaReportFeatureKind.predefined ? 'predefined' : 'custom';
            sourceLayerId = report.featureKind == AreaReportFeatureKind.predefined ? getLayerId(report.sourceLayer) : 0;
        } else {
            throw new Error('Not yet implemented');
        }

        return this.savedFeatureService.createNewFeature(
            this.defaultTitleForReport() || 'New feature',
            features,
            kind,
            this.mapService.map.getView().getProjection(),
            sourceLayerId
        ).then((userFeature) => {
            let featureAndUiState = {
                    userFeature: userFeature,
                    visible: true,
                    deleting: false,
                    mapFeatures: features
            };
            return this.savedFeatureService
                .saveFeature(featureAndUiState)
                .then(() => {
                    report.userFeatures = featureAndUiState;
                });
        })
    }

    reportIfSingle() {
        return this.getActiveReportIfPoint() || this.getActiveReportIfArea();
    }

    showReportForFeature(featureAndUiState: FeatureAndUiState) {
        const userFeature = featureAndUiState.userFeature;

        this.savedFeatureService.updateFeatureVisibility(f => featureAndUiState === f
            || f.userFeature.geometryType === 'Point');

        const activeReport = this.reportIfSingle();
        if (activeReport && activeReport.userFeatures == featureAndUiState) {
            // Null operation, we're already showing this report.
            return;
        }

        let mapFeatures: Feature<Geometry>[] = featureAndUiState.mapFeatures;

        this.$rootScope.$broadcast(Panes.Events.SHOW_PANE, Panes.Ids.FEATURES);

        const features = this.mapService.featuresLayer.getSource().getFeatures();
        if (userFeature.geometryType !== 'Point' || !this.appConfig.multiplePinsInRHP()) {
            features.forEach(f => {
                f.set('selected', mapFeatures.some(mf => mf === f));
            });
        } else if (this.appConfig.multiplePinsInRHP() && userFeature.geometryType === 'Point') {
            features.filter(f => !(f.getGeometry() instanceof Point))
                .forEach(f => {
                    f.set('selected', false);
                });
        }

        if (userFeature.geometryType == 'Point') {
            const inCurrentReport = (this.getActiveReportIfMultiplePins() || {reports:<PinReport[]> []})
                .reports.some(rp => rp.userFeatures === featureAndUiState);

            if (inCurrentReport) {
                return;
            }

            let f : Feature<Geometry> = featureAndUiState.mapFeatures[0];
            let pointGeom : Point = <any> f.getGeometry();

            this.addPointQuery(pointGeom.getCoordinates(), featureAndUiState, false);

            return;
        }

        if (!mapFeatures.length) {
            throw new Error('UserFeature report has no map features yet...');
        }

        if (userFeature.kind == 'predefined') {
            const sourceLayer = arrayHead(getLayerById(this.mapService.treeData, userFeature.sourceLayerId));
            const predefinedReport : PredefinedAreaReport = this.createPredefinedAreaReport(mapFeatures, sourceLayer, featureAndUiState);
            this.showReport(predefinedReport);
        } else {
            const report : DrawnAreaReport = this.createDrawnAreaReport(mapFeatures, featureAndUiState);
            this.showReport(report);
        }
    }

    displayPointQuery(point: Feature<Point>) {
        /*if (pinData && !pinData.data) {
            throw new Error('no pin data')
        }*/

        this.showReport({
            kind: ReportKind.pin,
            point,
            queryResults: {}
        });
    }

    public getPointQueryResultsForCurrentScienceLayer() : PointReportRequestAndLayers {
        let rp = this.sharedState().activeReport;
        if (rp.kind == ReportKind.pin) {
            return this.makePointQueryRequest(rp, this.getScienceLayersForPointQuery())
        }
    };

    public makePointQueryRequest(rp : PinReport, layers: Item[]) : PointReportRequestAndLayers {
        const key = FeatureState.layersToKey(layers);

        if (rp.queryResults[key]) return rp.queryResults[key];

        let point = rp.point;

        let cancel = this.$q.defer();

        let layerNames = layers.map(getLayerName)
            .map((l) => {
                // TODO - remove this Dirty hack
                if (l.indexOf('smap') == 0) {
                    return 'smap_soil_drainage';
                }
                return l;
            })
            .join(',');

        const coordinate = point.getGeometry().getCoordinates();
        let request = this.portalServices.dataPointQuery(
                  coordinate,
                  layerNames,
                {
                    cache: true,
                    timeout: cancel.promise
                }
            );

        let requestAndCancel: IRequestAndCancel<IPointReport|IRequestError> = {
            request,
            cancel
        };

        requestAndCancel.retry = () => {
            requestAndCancel.cancel.resolve(true);
            let newCancel = this.$q.defer();
            requestAndCancel.request = this.portalServices.dataPointQuery(
                coordinate,
                layerNames,
                {
                    cache: true,
                    timeout: newCancel.promise
                }
            );

            requestAndCancel.cancel = newCancel;
        };

        let resp = {
            queriedLayers: layers,
            request: requestAndCancel
        };

        rp.queryResults[key] = resp;

        return resp;
    }

    protected prepareLayerQueryRequest(geometry: Geometry, layers: Item[] = []) : ILayerQueryRequest {
        if (!layers.length) {
            layers = getVisibleScienceLayers(this.mapService.treeData);
        }

        return {
            geometry,
            epsg: extractEPSGNum(this.mapService.map.getView().getProjection().getCode()).toString(),
            scienceLayers: layers.map(getLayerName),
            layerIds: layers.map(getLayerId),
        }
    }

    protected static layersToKey(layers: Item[]) : string {
        return layers.map(getLayerId).sort().join(',');
    }

    protected makeAreaQueryRequest(report: AreaReport, layers: Item[]) : AreaReportRequestAndLayers {
        let key = FeatureState.layersToKey(layers);

        if (report.queryResults[key]) return report.queryResults[key];

        let ids = FeatureState.layersToKey(layers);

        const sMapLayers = layers
            .filter((l) => l.layerService.layerName.indexOf('smap') == 0);
            // .filter(l => !['smap_nitrogen_leach_risk', 'smap_bypass_flow'].includes(l.layerService.layerName));

        if (sMapLayers.length && sMapLayers.length < layers.length) {
            throw new Error(`Invalid request: can't combined S-Map and non S-Map layers in an area query`);
        }

        report.features.forEach((fs) => {
            let geom = fs.getGeometry();
            geom.set(QUERIED_REVISION, fs.getRevision());
        });

        let requestOptions = this.prepareLayerQueryRequest(report.geometry, layers);
        let request: IRequestAndCancel<AreaReportResult>;

        if (sMapLayers.length) {
            request = this.layerQueryService.querySMap(requestOptions);
        } else {
            request = this.layerQueryService.query(requestOptions);
        }

        let requestAndLayers = {
            queriedLayers: layers,
            request
        };

        report.queryResults[ids] = requestAndLayers;

        return requestAndLayers;
    }

    getScienceLayersForPointQuery(): Item[] {
        // return this._reportingLayers.filter(isQueryAble);
        return this.appConfig.getLayersForPointQuery(this.mapService.treeData, this._reportingLayers);
    }

    getScienceLayersForAreaQuery() : Item[] {
        return this._reportingLayers.filter(isQueryAble);
    }

    queryExtent() {
        this.usageLoggingService.logWithAnalytics('area query', 'queryExtent', '');
        let extent = this.coordinateAdjustmentService.getVisibleMapExtent(this.mapService.map);

        let geometry = fromExtent(extent);
        let feature = new Feature({
            geometry
        });
        feature.set('selected', true);

        let fs = [feature];
        const src = this.mapService.featuresLayer.getSource();
        // src.clear();
        src.addFeature(feature);

        let report : ExtentAreaReport = {
            kind: ReportKind.area,
            featureKind: AreaReportFeatureKind.extent,
            features: fs,
            geometry,
            queryResults: {}
        };

        this.showReport(report);
    }

    queryDrawnArea() {
        let $root = this.$rootScope,
            mapService = this.mapService;

        $root.$broadcast('hide-toolbar');
        let source = this.mapService.featuresLayer.getSource();

        let draw = new Draw({
            source : source,
            type: 'Polygon'
        });

        draw.on('drawstart', () => {
            this.usageLoggingService.logWithAnalytics('area query', 'startDrawing', '');
        });

        this.mapService.setActiveClickInteraction(null);
        this.mapService.getDefaultClickInteraction().setActive(false);

        this.mapService.map.addInteraction(draw);

        $root.$broadcast('drawstart');

        let disableClickOutAndStopDrawing = clickOutsideOfMapHandler(() => {
            self.usageLoggingService.logWithAnalytics('area query', 'cancelDrawing', '');
            mapService.map.removeInteraction(draw);
            $root.$broadcast('drawend');
            let st = this.sharedState();
            let mode = st && st.featureMode;
            if (mode == 'drawn') {
                this.toggleMode(null);
            }
        }, $root);

        /*draw.on('drawstart', () => {
            // source.clear();
            // $scope.backToStart();
            // $scope.errorMessage = "Continue adding corners by clicking on the map. <br/> Close the outline of the shape by clicking on the first point.";
        });*/

        let self = this;

        draw.on('drawend', function(ev : any) {
            self.usageLoggingService.logWithAnalytics('area query', 'finishDrawing', '');
            disableClickOutAndStopDrawing();
            let feature : Feature<Geometry> = ev.feature;
            if (feature) {
                let report = self.createDrawnAreaReport([feature]);
                feature.set('selected', true);
                if ($root.$$phase) {
                    self.showReport(report);
                } else {
                    $root.$apply(() => self.showReport(report));
                }
            }
            mapService.map.removeInteraction(draw);
            $root.$broadcast('drawend');
        });
    }

    isPickFeatureInteractionActive() {
        let rp = this.sharedState().activeReport;
        if (rp.kind == ReportKind.area && rp.featureKind == AreaReportFeatureKind.predefined) {
            return this.activeInteractions.length && this.activeInteractions.some(int => int.getActive());
        }
        return false;
    }

    createReportFromCustomFeatures(features: Feature<Geometry>[],
                                   customLayerNode: L.tree.Item,
                                   featureAndUiState: FeatureAndUiState = undefined) : CustomLayerAreaReport {
        if (!features.length) {
            this.hideReport();
            return undefined;
        }

        const isPin = features[0].getGeometry() instanceof Point;

        if (isPin) {

        }

        const rp: CustomLayerAreaReport = {
            kind: ReportKind.area,
            featureKind: AreaReportFeatureKind.customLayer,
            revision: 0,
            features,
            sourceLayer: customLayerNode,
            geometry: combineFeatures(features as Feature<Polygon>[]),
            queryResults: {},
            userFeatures: featureAndUiState
        };

        this.showReport(rp);

        return rp;
    }

    createPredefinedAreaReport(features: Feature<Geometry>[], theNode: L.tree.Item, featureAndUiState: FeatureAndUiState = undefined) : PredefinedAreaReport {
        return {
            kind: ReportKind.area,
            featureKind: AreaReportFeatureKind.predefined,
            revision: 0,
            features,
            sourceLayer: theNode,
            geometry: combineFeatures(features as Feature<Polygon>[]),
            queryResults: {},
            userFeatures: featureAndUiState
        };
    }

    createDrawnAreaReport(features: Feature<Geometry>[], featureAndUiState: FeatureAndUiState = undefined) : DrawnAreaReport {
        return {
            kind: ReportKind.area,
            featureKind: AreaReportFeatureKind.drawn,
            features,
            geometry: combineFeatures((features as Feature<Polygon>[])),
            queryResults: {},
            userFeatures: featureAndUiState
        }
    }

    showToast(message: string, duration: number = TOAST_DURATION_LONG) {
        this.toastService.showToastObj({
            ...this.toastService.toastDefaults(),
            duration,
            uniqueId: FeatureState.ToastId,
            message
        });
    }

    private queryCustomLayer() {
        let theNode = this.sharedState().activeQueryLayer;
        if (!theNode) return;

        const theLayer = theNode.layer;

        this.toggleQueryLayers(theNode);

        if (theLayer instanceof VectorLayer) {
            const hoverInteraction = new Select({
                condition: pointerMove,
                layers: [theLayer],
                filter(feature/*, layer*/) {
                    const geom = feature.getGeometry();
                    return geom  instanceof Point
                        || geom instanceof Polygon
                        || geom instanceof MultiPolygon;
                },
                style: null
            })

            hoverInteraction.on('select', ev => {
                ev.selected.forEach(f => {
                    f.set('hover', true)
                })

                ev.deselected.forEach(f => {
                    f.set('hover', false)
                })
            })

            const interaction = new Select({
                layers: [theLayer],
                toggleCondition: singleClick,
                filter(feature/*, layer*/) {
                    const geom = feature.getGeometry();
                    return geom  instanceof Point
                        || geom instanceof Polygon
                        || geom instanceof MultiPolygon;
                },
                style: null
            });
            [interaction, hoverInteraction].forEach(int => this.mapService.map.addInteraction(int));
            this.activeInteractions.push(interaction, hoverInteraction);
            this.mapService.setActiveClickInteraction(interaction);

            interaction.on('select', (event) => {
                const firstPoint = event.selected.find(f => f.getGeometry().getType() === "Point");
                const collection = interaction.getFeatures();

                this.$rootScope.$apply(() => {
                    if (firstPoint) {
                        // if a point is selected, deselect all the other features by removing them from the collection
                        collection.getArray()
                            .filter(f => f !== firstPoint)
                            .forEach(f => collection.remove(f));

                        this.createPointReport(firstPoint as Feature<Point>);
                    } else {
                        // if an area has been selected, then remove any selected points
                        collection.getArray()
                            .filter(f => f.getGeometry() instanceof Point)
                            .forEach(f => collection.remove(f));

                        const selected = collection.getArray();
                        const selectedSet = [...selected];
                        theLayer.getSource().getFeatures().forEach(f => {
                            f.set('selected', selectedSet.includes(f));
                        });
                        // query the selected (multi)polygons
                        this.createReportFromCustomFeatures(selected, theNode);
                    }
                });
            });

            return;
        }

        if (theLayer.get('wmtsCapabilities')) {
            const interaction = new WmtsGetFeatureInfoInteraction({
                source: theLayer.getSource() as WMTS,
                wmtsCapabilities: theLayer.get('wmtsCapabilities')
            });

            this.mapService.map.addInteraction(interaction);

            return;
        }
    }

    toggleQueryLayers(theNode: Item) {
        const toggle = (n: Item) => this.layerTreeManipulationService.toggleLayer(n, n === theNode, { silent: true });

        getLayersForAreaQuery(this.mapService.treeData).forEach(toggle);
        filterNodes(this.mapService.treeData, isCustomLayerQueryable).forEach(toggle);
        filterNodes(this.mapService.treeData, isLocalData).forEach(toggle);
    }

    /**
     * Enables a mode where the user clicks on features to add them to the current report.
     * They can click on multiple features.
     * Clicking on a feature again will remove it from the report, if the report has no
     * features left, then, the click to add features mode will be disabled and the report hidden.
     */
    queryPredefinedFeatures() {
        let theNode = this.sharedState().activeQueryLayer;
        if (!theNode) return;

        if (!theNode.isChecked()) {
            this.showToast(`Layer "${theNode.name}" enabled`);
        }

        this.toggleQueryLayers(theNode);

        this.$rootScope.$broadcast('hide-toolbar');

        let layerQuery : IContextLayerQuery;

        try {
            layerQuery = getContextQueryForLayer(theNode, this.$http, this.mapService, this.portalServices);
        } catch (e) {
            this.showToast("Sorry, we're enable to query this layer at the moment", this.toastService.DURATION_LONG);
            return;
        }

        let interaction = new FeatureQueryClickInteraction(
            this.mapService.featuresLayer,
            layerQuery,
            (addedFeatures: Feature<Geometry>[]) => {
                if (!addedFeatures.length) {
                    this.showToast(`No ${theNode.name} at this point`);
                    return;

                }

                addedFeatures.forEach(f => f.set('selected', true));
                this.usageLoggingService.logWithAnalytics('area query', 'predefinedFeaturesQuery', theNode.layerService.layerName);

                if (this.getActiveReportIfPredefined()) {
                    this.$rootScope.$broadcast(FeaturesEvents.featuresAdded, addedFeatures, true);
                    return;
                } else {
                    this.showReport(this.createPredefinedAreaReport(addedFeatures, theNode));
                }
            },
            (feature: Feature<Geometry>) => {
                this.$rootScope.$broadcast(FeaturesEvents.featureRemoved, feature, true);
            });
        this.activeInteractions.push(interaction);
        this.mapService.setActiveClickInteraction(interaction);

        interaction.on('change:queryError' as any, () => {
            this.usageLoggingService.logWithAnalytics('predefinedFeatures', 'queryError', theNode.layerService.layerName);
        });

        let onChangeHelp = () => {
            // $scope.instructions = clickInteraction.get('help');
        };

        onChangeHelp();

        interaction.on('change:help' as any, onChangeHelp);
        interaction.on('change:active', () => {
            try {
                if (!interaction.getActive()) {
                    interaction.un('change:help' as any, onChangeHelp);
                    // remove this interaction after it is turned off.
                    this.mapService.map.removeInteraction(interaction);
                    // enable the default interaction.
                    this.mapService.getDefaultClickInteraction().setActive(true);
                }
            } catch (e) {
                console.warn(e);
            }
        });

        interaction.on('change:error' as any, () => {
            this.showToast('Query failed', TOAST_DURATION_LONG);
        });

        interaction.on('change:loading' as any, () =>{
            this.sharedState().featureLoading = interaction.get('loading');
        });
    }

    protected useCurrentScienceLayersForReport() {
        this._reportingLayers = getVisibleScienceLayers(this.mapService.treeData)
            .filter(isQueryAble);
    }

    showReport(report: FeatureReport) {
        const ar = this.getActiveReportIfArea()
        if (ar) {
            this.hideReportFeatures(ar)
        }

        if (!this._reportingLayers || !this._reportingLayers.length) {
            this.useCurrentScienceLayersForReport();
        }
        let st = this.sharedState();
        st.paneTitle = FEATURE_REPORT;
        st.activeReport = report;
        this.$rootScope.$broadcast(FeaturesEvents.reportChanged, report);
        this.displayFeaturesPane();
    }

    hideReportFeatures(ar: AreaReport) {
        let hideFeature = (f : Feature<Geometry>) => {
            f.set('hidden', true);
        };

        let removeFeature = (f) => {
            try {
                this.savedFeatureService.featuresSource.removeFeature(f)
            } catch (e) {

            }
        };

        const uf = ar.userFeatures;
        if (!uf) {
            // the report is unsaved, so we remove the features from the map
            ar.features.forEach(removeFeature);
        } else {
            uf.visible = false;
            ar.features.forEach(hideFeature);
        }
    }

    hideReport() {
        if (this.sharedState().activeReport) {
            const ar = this.sharedState().activeReport;

            if (ar.kind == ReportKind.area) {
                this.hideReportFeatures(ar);
            }

            if (ar.kind === ReportKind.area
                && ar.featureKind === AreaReportFeatureKind.customLayer) {
                ar.features.forEach(f => f.set('selected', false));
            }
            // remove unsaved features from the map
            /*if (uf && !uf.userFeature.id) {
                (ar.userFeatures && ar.userFeatures.mapFeatures || [])
                    .forEach(removeFeature);
            } else if (ar.kind == ReportKind.area) {
                ar.features.forEach(removeFeature);
            }*/
        }

        this.sharedState().paneTitle = ADD_FEATURES;
        this.sharedState().activeReport = null;
        this.sharedState().featureMode = null;
        this.sharedState().activeQueryLayer = null;
        this.useCurrentScienceLayersForReport();
        if (this.activeInteractions.length) {
            this.activeInteractions.forEach(int => {
                try {
                    this.mapService.map.removeInteraction(int);
                } catch (e) { }
            })
            this.activeInteractions  = [];
        }
        this.mapService.setActiveClickInteraction(this.mapService.getDefaultClickInteraction());
        this.$rootScope.$broadcast(FeaturesEvents.featuresBackToStart);
        this.$rootScope.$broadcast(FeaturesEvents.reportChanged, null);
        this.mapService.featuresLayer.getSource().forEachFeature(f => f.set('selected', false));
    }

    toggleSourceLayer(): void {
        let rp = this.sharedState().activeReport;

        if (rp && rp.kind == ReportKind.area && rp.featureKind == AreaReportFeatureKind.predefined) {
            let activation = !rp.sourceLayer.checked;
            this.layerTreeManipulationService.toggleLayer(rp.sourceLayer, activation, { silent: true });
        }
    }

    getActiveReportIfArea(): AreaReport {
        const rp = this.sharedState().activeReport;
        return rp && rp.kind == ReportKind.area ? rp : null;
    }

    getActiveReportIfDrawn(): DrawnAreaReport {
        const rp = this.sharedState().activeReport;
        return rp && rp.kind == ReportKind.area && rp.featureKind == AreaReportFeatureKind.drawn ? rp : null;
    }

    getActiveReportIfExtent(): ExtentAreaReport {
        const rp = this.sharedState().activeReport;
        return rp && rp.kind == ReportKind.area && rp.featureKind == AreaReportFeatureKind.extent ? rp : null;
    }

    getActiveReportIfPoint(): PinReport {
        const rp = this.sharedState().activeReport;
        return rp && rp.kind == ReportKind.pin ? rp : null;
    }

    getActiveReportIfMultiplePins(): MultiplePinReport {
        const rp = this.sharedState().activeReport;
        return rp && rp.kind === ReportKind.multiplePin ? rp : null;
    }

    getActiveReportIfPredefined(): PredefinedAreaReport {
        const rp = this.sharedState().activeReport;
        return rp && rp.kind == ReportKind.area && rp.featureKind == AreaReportFeatureKind.predefined ? rp : null;
    }

    defaultTitleForReport(): string {
        const rp = this.getActiveReportIfPredefined();
        if (!rp) return '';

        const uf = rp.userFeatures;
        if (uf && uf.userFeature.title) return uf.userFeature.title;

        let source = rp.sourceLayer;
        let titleProp = getFeatureTitleProperty(source);

        if (titleProp) {
            return rp.features
                .map((f) => f.get(titleProp) || f.get('_title'))
                .filter(title => title)
                .join(' + ');
        }
    }

    private createPointReport(firstPoint: Feature<Point>) {
        const report: PinReport = {
            kind: ReportKind.pin,
            point: firstPoint,
            queryResults: {

            }
        };

        this.showReport(report);
    }
}
